/**
 * WebSocket subscription for live transaction updates.
 *
 * Pattern: instantiate in a component's `mounted()` with the transaction
 * id + an `onUpdate` callback. Call `disconnect()` in `beforeUnmount()`.
 * The socket auto-reconnects with exponential backoff if the connection
 * drops; the heartbeat ping keeps API Gateway's 10-minute idle timeout
 * from killing it.
 *
 * Messages on the wire are IDs only — `{ type: 'transaction:updated',
 * transaction_id }`. The component re-fetches the transaction over HTTPS
 * when it receives one. No PII rides the socket; no auth on connect
 * (matches the existing 'bookings' subscription pattern).
 *
 * Usage:
 *   mounted() {
 *     this._watcher = new TransactionWatcher(this.transactionId, () => {
 *       this.fetchTransaction();
 *     });
 *   },
 *   beforeUnmount() {
 *     if (this._watcher) this._watcher.disconnect();
 *   }
 */

const WS_BASE = 'wss://3bqa7hkquf.execute-api.us-east-1.amazonaws.com/production';
const HEARTBEAT_MS = 4 * 60 * 1000;
const BACKOFF_MS = [1000, 2000, 4000, 8000, 16000, 30000];

export default class TransactionWatcher {
  constructor(transactionId, onUpdate) {
    if (!Number.isInteger(Number(transactionId)) || Number(transactionId) <= 0) {
      throw new Error('TransactionWatcher requires a positive integer transactionId');
    }
    this.transactionId = Number(transactionId);
    this.onUpdate = typeof onUpdate === 'function' ? onUpdate : () => {};

    this.ws = null;
    this.attempts = 0;
    this.intentionallyClosed = false;
    this.reconnectTimer = null;
    this.heartbeatTimer = null;

    this._connect();
  }

  _connect() {
    if (this.intentionallyClosed) return;

    const url = `${WS_BASE}?type=transaction&transactionId=${this.transactionId}`;
    try {
      this.ws = new WebSocket(url);
    } catch (err) {
      console.error('[txWatcher] construct failed', err);
      this._scheduleReconnect();
      return;
    }

    this.ws.onopen = () => {
      console.log(`[txWatcher] connected for tx ${this.transactionId}`);
      this.attempts = 0;
      this._startHeartbeat();
    };

    this.ws.onmessage = (event) => {
      let msg;
      try {
        msg = JSON.parse(event.data);
      } catch (_) {
        return;
      }
      if (msg.type === 'pong') return;
      if (msg.type === 'transaction:updated' && Number(msg.transaction_id) === this.transactionId) {
        try { this.onUpdate(msg); }
        catch (err) { console.error('[txWatcher] onUpdate threw', err); }
      }
    };

    this.ws.onerror = (err) => {
      console.warn('[txWatcher] error', err);
    };

    this.ws.onclose = (ev) => {
      console.log(`[txWatcher] closed`, { code: ev.code, reason: ev.reason });
      this._stopHeartbeat();
      this.ws = null;
      if (!this.intentionallyClosed) this._scheduleReconnect();
    };
  }

  _startHeartbeat() {
    this._stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        try { this.ws.send(JSON.stringify({ action: 'ping' })); }
        catch (_) { /* ignore */ }
      }
    }, HEARTBEAT_MS);
  }

  _stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  _scheduleReconnect() {
    if (this.intentionallyClosed) return;
    const delay = BACKOFF_MS[Math.min(this.attempts, BACKOFF_MS.length - 1)];
    this.attempts += 1;
    console.log(`[txWatcher] reconnect attempt ${this.attempts} in ${delay}ms`);
    this.reconnectTimer = setTimeout(() => this._connect(), delay);
  }

  disconnect() {
    this.intentionallyClosed = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this._stopHeartbeat();
    if (this.ws) {
      try { this.ws.close(1000, 'component unmount'); }
      catch (_) { /* ignore */ }
      this.ws = null;
    }
  }
}
